import "./TextModal.scss"

import { FC, lazy, ReactNode, Suspense, useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import cn from "classnames"

import Modal from "components/Modal/Modal"
import Button from "components/Button/Button"
import { Flex } from "components/Flex"
import toastMessages from "stores/toastMessages"
import Input from "components/Input/Input"
import FormField from "components/FormField/FormField"
import { INPUT_MAX_LENGTH_LARGE, TextVariable } from "config/constants"
import type { WysiwygEditorRef } from "../WysiwygEditor/WysiwygEditor"
import FormattedText from "../FormattedText/FormattedText"
import { insertInputTextAtCursor } from "components/Input/utils"

/**
 * Lazy-loaded so CKEditor 5 (the heaviest dependency) ships as its own on-demand
 * chunk instead of in the eager `main` bundle — it loads only when a rich-text
 * editing modal is opened.
 */
const WysiwygEditor = lazy(() => import("../WysiwygEditor/WysiwygEditor"))

interface ITextModalProps {
    title?: string
    getDescription?: (params?: { onInsertTextVariableClick?: (textVariable: TextVariable) => void }) => ReactNode
    text: string
    lineBreakAfter?: number
    onChangeText?(text: string): void
    onSave?(text: string): Promise<void> | void
    onClose(): void
    overrideFooter?: ReactNode
    monospace?: boolean
    textFormatted?: boolean
    textVariables?: boolean
}

const TextModal: FC<ITextModalProps> = ({
    title,
    getDescription,
    text,
    lineBreakAfter,
    onChangeText,
    onSave,
    onClose,
    overrideFooter,
    monospace,
    textFormatted,
    textVariables,
}) => {
    const { t } = useTranslation()
    const isEdit = onChangeText && !!onSave
    const [isSubmitting, setIsSubmitting] = useState(false)
    const textContainerStyle = monospace ? { fontSize: "1.35rem", lineHeight: "2rem" } : {}
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const wysiwygEditorRef = useRef<WysiwygEditorRef>(null)
    const textDivRef = useRef<HTMLDivElement>(null)
    const [textContainerWidth, setTextContainerWidth] = useState<number>()
    const textContainerPaddingRight =
        typeof lineBreakAfter === "number"
            && typeof textContainerWidth === "number"
            ? `calc(${textContainerWidth}px - ${lineBreakAfter}ch)`
            : undefined

    async function handleSave() {
        if (!onSave) return

        setIsSubmitting(true)

        try {
            const textToSave = text.trimEnd()

            await onSave(textToSave)
            onClose()
        } catch (e) {
            setIsSubmitting(false)
            toastMessages.showSystemError()
        }
    }

    useLayoutEffect(() => {
        const textEl = isEdit ? textareaRef.current : textDivRef.current
        if (!textEl) return
        const textContainerComputedStyle = window.getComputedStyle(textEl)
        const scrollbarWidth = textEl.offsetWidth - textEl.clientWidth
        const paddingLeft = parseInt(textContainerComputedStyle.paddingLeft) || 0
        const borderWidth = parseFloat(textContainerComputedStyle.borderWidth) || 0
        setTextContainerWidth(textEl.offsetWidth - scrollbarWidth - paddingLeft - borderWidth * 2)
    }, [text, isEdit])

    function handleInsertTextVariable(variable: TextVariable) {
        if (textFormatted) {
            wysiwygEditorRef.current?.insertText(variable)
            return
        }
        if (textareaRef.current) {
            insertInputTextAtCursor(textareaRef.current, variable)
        }
    }

    const description = getDescription?.({
        onInsertTextVariableClick: textVariables ? handleInsertTextVariable : undefined,
    }) || null

    return (
        <Modal
            title={title}
            onClose={onClose}
            width="952px"
            footer={overrideFooter || (!!onChangeText && !!onSave
                ? <Flex justifyContent="flex-end">
                    <Button
                        theme="secondary"
                        label={t('common.cancel')}
                        width="auto"
                        onClick={onClose}
                        disabled={isSubmitting} />
                    <Button
                        label={t('common.save')}
                        onClick={handleSave}
                        width="auto"
                        className="ml-2"
                        disabled={isSubmitting}
                        loading={isSubmitting} />
                </Flex>
                : undefined)
            }
            withWysiwygEditor={isEdit && !!onChangeText && textFormatted}>
            <Flex className="h-100"
                flow="column">
                {!!description &&
                    <div className="mb-4">{description}</div>
                }
                {!!isEdit && !!onChangeText
                    ? <>
                        {textFormatted
                            ? <Suspense fallback={null}>
                                <WysiwygEditor
                                    ref={wysiwygEditorRef}
                                    content={text}
                                    onChange={onChangeText}
                                />
                            </Suspense>
                            : <div className="text-modal__textarea">
                                <Input
                                    ref={textareaRef}
                                    type="textarea"
                                    rows={24}
                                    value={text}
                                    onChange={e => onChangeText(e.target.value)}
                                    className={cn("h-100", { "font-family-monospace": monospace })}
                                    inputClassName="h-100 border-none round-reset"
                                    theme="white"
                                    maxLength={INPUT_MAX_LENGTH_LARGE}
                                    style={{ ...textContainerStyle, paddingRight: textContainerPaddingRight }}
                                />
                            </div>
                        }
                        <FormField optional></FormField>
                    </>
                    : <div
                        ref={textDivRef}
                        className={cn("max-h-100 overflow-auto white-space-pre-wrap word-break-break-word", { "font-family-monospace": monospace })}
                        style={{ ...textContainerStyle, paddingRight: textContainerPaddingRight }}>
                        {textFormatted
                            ? <FormattedText text={text} />
                            : text
                        }
                    </div>
                }
            </Flex>
        </Modal>
    )
}

export default TextModal
